import type { ISelectionRect } from '../FeedbackWidget/hooks/useScreenshotCapture'

export interface IPoint {
  x: number
  y: number
}

const MARKER_RADIUS = 10
const MARKER_RING_WIDTH = 3

const tokenColor = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

// A dot, ringed so it reads on light and dark screens. `scale` is canvas pixels per CSS pixel.
const drawDot = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number) => {
  ctx.beginPath()
  ctx.arc(x, y, MARKER_RADIUS * scale, 0, 2 * Math.PI)
  ctx.fillStyle = tokenColor('--status-error')
  ctx.fill()
  ctx.lineWidth = MARKER_RING_WIDTH * scale
  ctx.strokeStyle = tokenColor('--surface-default')
  ctx.stroke()
}

// Marks where the tester right-clicked on a capture of the viewport.
export const drawClickMarker = (canvas: HTMLCanvasElement, point: IPoint): string => {
  const scale = canvas.width / window.innerWidth
  const ctx = canvas.getContext('2d')
  if (ctx) drawDot(ctx, point.x * scale, point.y * scale, scale)
  return canvas.toDataURL('image/png')
}

const contains = (rect: ISelectionRect, point: IPoint): boolean =>
  point.x >= rect.x &&
  point.y >= rect.y &&
  point.x <= rect.x + rect.width &&
  point.y <= rect.y + rect.height

// The same dot on a selected area, when the area holds the click; otherwise it is left as framed.
export const markRegionScreenshot = async (
  dataUrl: string,
  region: ISelectionRect,
  point: IPoint,
): Promise<string> => {
  if (!contains(region, point)) return dataUrl
  const image = new Image()
  image.src = dataUrl
  await image.decode()
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return dataUrl
  ctx.drawImage(image, 0, 0)
  const scale = canvas.width / region.width
  drawDot(ctx, (point.x - region.x) * scale, (point.y - region.y) * scale, scale)
  return canvas.toDataURL('image/png')
}
